import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#F7F8FA] flex flex-col">
      <header className="px-6 sm:px-8 py-6 max-w-7xl mx-auto w-full">
        <Link to="/" data-testid="admin-login-back" className="inline-flex items-center gap-2 text-sm text-[#1E1E1E]/70 hover:text-[#003B5C]">
          <ArrowLeft size={16} /> Back to site
        </Link>
      </header>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="w-14 h-14 rounded-sm bg-[#003B5C] mx-auto flex items-center justify-center mb-5">
              <Lock size={20} className="text-[#D4A437]" />
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-3">Admin Portal</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#003B5C]">Sign in</h1>
            <p className="text-sm text-[#1E1E1E]/65 mt-2">Manage sermons, events, and messages.</p>
          </div>
          <form onSubmit={submit} data-testid="admin-login-form" className="bg-white border border-gray-100 rounded-sm p-8 space-y-5 shadow-sm">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1E1E1E]/60 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white text-[#1E1E1E]"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1E1E1E]/60 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white text-[#1E1E1E]"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full bg-[#003B5C] text-white px-6 py-3.5 rounded-sm font-medium hover:bg-[#002A42] transition-all disabled:opacity-50"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
